<script setup>
import { computed, onMounted, onActivated, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { obterDefinicao } from './aprovacoesRegistry'
import { useWorkflowAprovacao } from './useWorkflowAprovacao'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'

// ⚠️ Side-effect: importa as definições para registrar no registry
import '@/components/Compras/ordensCompra/aprovacao/definicao'
import '@/components/Compras/purchaseOrders/aprovacao/definicao'
import '@/components/Vendas/pedidoVenda/aprovacao/definicaoComercial'
import '@/components/Vendas/pedidoVenda/aprovacao/definicaoCredito'
import '@/components/Vendas/pedidoVenda/aprovacao/definicaoReanaliseFinanciamento'
import '@/components/Vendas/pedidoVenda/aprovacao/definicaoComercialReanalise'

const props = defineProps({
  chave: { type: String, required: false, default: null }
})

const emit = defineEmits(['show-notification'])

// Resolve a chave: prioriza prop direta, depois extraProps do menu (RBAC via useUiStore)
const uiStore = useUiStore()
const chaveResolvida = computed(() => {
  return props.chave || uiStore.currentModuleExtraProps?.chave || null
})

// Resolve a definição da função de aprovação
const definicao = computed(() => {
  if (!chaveResolvida.value) {
    return {
      titulo: 'Workflow de Aprovação',
      subtitulo: 'Nenhuma chave de aprovação configurada.',
      colunas: [],
      entityKey: 'id',
      apiEndpoint: '',
      endpointLista: '',
      endpointAprovar: '',
      endpointReprovar: '',
      listasTabelas: [],
      nomeEntidade: '',
      nomeEntidadeTitle: '',
      modalTitulo: 'Análise',
      modalTamanho: 'xl',
      modalCorHeader: 'var(--bs-info)',
      campoHeader: '',
      hintMessage: '',
      emptyMessage: 'Nenhum item pendente de aprovação encontrado.',
      componenteFiltros: null,
      componenteDetalhesEnxuto: null,
      componenteDetalhesCompleto: null,
      slotRenderers: null,
      formatarValor: (v) => v ?? '-',
      filtrosPadrao: {}
    }
  }
  return obterDefinicao(chaveResolvida.value)
})

// Instancia o composable com a config da definição
const workflow = useWorkflowAprovacao({
  apiEndpoint: definicao.value.apiEndpoint,
  entityKey: definicao.value.entityKey,
  endpointLista: definicao.value.endpointLista,
  endpointAprovar: definicao.value.endpointAprovar,
  endpointReprovar: definicao.value.endpointReprovar,
  listasTabelas: definicao.value.listasTabelas,
  nomeEntidade: definicao.value.nomeEntidade,
  nomeEntidadeTitle: definicao.value.nomeEntidadeTitle,
  formatarValor: definicao.value.formatarValor,
  filtrosPadrao: definicao.value.filtrosPadrao,
  emitNotification: (type, message) => {
    emit('show-notification', { message, type })
  }
})

// Extrai refs e métodos do composable
const {
  lista, loading, erro, sortField, sortOrder, filtros, listas, paginacao,
  modalAberto, itemDetalhes, isLoadingDetalhes, visaoCompleta,
  reprovarModalRef, aprovarModalRef,
  posicaoLabel, temProxima, temAnterior,
  estaProcessandoAcao, confirmacaoAtiva,
  decisoesCustomizadas, temDecisoesCustomizadas, executarDecisaoCustomizada,
  formatarValor, getTextoLista, formatarMoedaComSigla,
  buscarDados, aplicarFiltros, limparFiltros,
  mudarPagina, mudarTamanhoPagina,
  abrirModalAnalise, proximo, anterior, alternarVisao,
  confirmarReprovacao, confirmarAprovacao,
  executarAprovacao, executarReprovacao,
  handleSortChange, carregarListas
} = workflow

// Grupos de aprovadores extraídos da lista carregada
const gruposDisponiveis = computed(() => {
  const map = new Map()
  lista.value.forEach(item => {
    if (item.idGrupoAcessoAprovador && item.nomeGrupoAprovador) {
      map.set(item.idGrupoAcessoAprovador, item.nomeGrupoAprovador)
    }
  })
  return Array.from(map, ([id, nome]) => ({ id, nome }))
})

const temFiltroAtivo = computed(() => {
  return Object.values(filtros).some(v => v !== null && v !== undefined && v !== '')
})

const alternarFaixaValor = (valor) => {
  filtros.faixaValor = filtros.faixaValor === valor ? '' : valor
  aplicarFiltros()
}

const alternarGrupo = (id) => {
  filtros.idGrupoAprovador = filtros.idGrupoAprovador === id ? null : id
  aplicarFiltros()
}

// Watch para empresa ativa
const authStore = useAuthStore()
watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    paginacao.value.TotalCount = 0
  }
})

onMounted(async () => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    await carregarListas()
    buscarDados(1)
  }
})

onActivated(() => {
  buscarDados(paginacao.value.CurrentPage || 1)
})

defineExpose({ buscarDados, abrirModalAnalise })
</script>

<template>
  <PaginaAdministrar
    :titulo="definicao.titulo"
    :subtitulo="definicao.subtitulo"
    :filtros-abertos="true"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="false"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @atualizar="aplicarFiltros"
  >
    <template #filtros>
      <slot name="filtros">
        <component
          :is="definicao.componenteFiltros"
          :filtros="filtros"
          :grupos-disponiveis="gruposDisponiveis"
          :tem-filtro-ativo="temFiltroAtivo"
          @aplicar-filtros="aplicarFiltros"
          @limpar-filtros="limparFiltros"
          @alternar-faixa-valor="alternarFaixaValor"
          @alternar-grupo="alternarGrupo"
        />
      </slot>
    </template>

    <div v-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <template v-else>
      <!-- Hint: duplo clique abre análise -->
      <div class="d-flex align-items-center gap-2 mb-2 text-body-secondary small">
        <i class="bi bi-info-circle"></i>
        <span>{{ definicao.hintMessage || 'Duplo clique na linha para analisar' }}</span>
      </div>

      <AdminTable
        :columns="definicao.colunas"
        :data="lista"
        :loading="loading"
        :empty-message="definicao.emptyMessage || 'Nenhum item pendente de aprovação encontrado.'"
        :row-key="definicao.entityKey"
        v-model:sort-field="sortField"
        v-model:sort-order="sortOrder"
        @sort-change="handleSortChange"
        @row-dblclick="({ row }) => abrirModalAnalise(row)"
      >
        <!-- Slot: resumoAprovacao (renderizado pela definição) -->
        <template #resumoAprovacao="{ row }">
          <component
            :is="definicao.slotRenderers?.resumoAprovacao"
            :row="row"
          />
        </template>
        <!-- Slot: acoes (renderizado pela definição) -->
        <template #acoes="{ row }">
          <component
            :is="definicao.slotRenderers?.acoes"
            :row="row"
            :abrir-modal="abrirModalAnalise"
          />
        </template>
      </AdminTable>
    </template>
  </PaginaAdministrar>

  <!-- Modal de Análise -->
  <ModalFormularioV2
    v-model="modalAberto"
    :titulo="definicao.modalTitulo + (posicaoLabel ? ' (' + posicaoLabel + ')' : '')"
    :tamanho="definicao.modalTamanho || 'xl'"
    :cor-header="definicao.modalCorHeader || 'var(--bs-info)'"
  >
    <template #header>
      <h5 class="modal-title-v2 mb-0 d-flex align-items-center gap-2">
        <i class="bi bi-file-earmark-text"></i>
        {{ definicao.modalTitulo }}
        <span v-if="posicaoLabel" class="badge bg-info-subtle text-info-emphasis fs-6 ms-auto">
          {{ posicaoLabel }}
        </span>
      </h5>
    </template>

    <!-- Loading -->
    <div v-if="isLoadingDetalhes" class="text-center py-5">
      <div class="spinner-border text-info mb-3" role="status"></div>
      <p class="text-body-secondary">Carregando detalhes...</p>
    </div>

    <template v-else-if="itemDetalhes">
      <!-- ===== VISÃO ÚNICA (quando visaoUnica = true, ex: crédito) ===== -->
      <template v-if="definicao.visaoUnica">
        <slot name="detalhes-enxutos" :item="itemDetalhes" :listas="listas" :formatar-valor="formatarValor" :get-texto-lista="getTextoLista" :formatar-moeda-com-sigla="formatarMoedaComSigla">
          <component
            :is="definicao.componenteDetalhesEnxuto"
            :item-detalhes="itemDetalhes"
            :listas="listas"
            :formatar-valor="formatarValor"
            :get-texto-lista="getTextoLista"
            :formatar-moeda-com-sigla="formatarMoedaComSigla"
          />
        </slot>
      </template>

      <!-- ===== VISÃO ENXUTA (padrão) ===== -->
      <template v-else-if="!visaoCompleta">
        <slot name="detalhes-enxutos" :item="itemDetalhes" :listas="listas" :formatar-valor="formatarValor" :get-texto-lista="getTextoLista" :formatar-moeda-com-sigla="formatarMoedaComSigla">
          <component
            :is="definicao.componenteDetalhesEnxuto"
            :item-detalhes="itemDetalhes"
            :listas="listas"
            :formatar-valor="formatarValor"
            :get-texto-lista="getTextoLista"
            :formatar-moeda-com-sigla="formatarMoedaComSigla"
          />
        </slot>

        <!-- Botão Ver Mais Detalhes -->
        <div class="text-center mt-2">
          <button type="button" class="btn btn-outline-info btn-sm px-3" @click="alternarVisao">
            <i class="bi bi-chevron-expand me-1"></i> Ver Mais Detalhes
          </button>
        </div>
      </template>

      <!-- ===== VISÃO COMPLETA ===== -->
      <template v-else>
        <slot name="detalhes-completos" :item="itemDetalhes" :listas="listas" :formatar-valor="formatarValor" :get-texto-lista="getTextoLista" :formatar-moeda-com-sigla="formatarMoedaComSigla">
          <component
            :is="definicao.componenteDetalhesCompleto"
            :item-detalhes="itemDetalhes"
            :listas="listas"
            :formatar-valor="formatarValor"
            :get-texto-lista="getTextoLista"
            :formatar-moeda-com-sigla="formatarMoedaComSigla"
          />
        </slot>

        <!-- Botão Ver Menos -->
        <div class="text-center mt-2">
          <button type="button" class="btn btn-outline-secondary btn-sm px-3" @click="alternarVisao">
            <i class="bi bi-chevron-contract me-1"></i> Ver Menos
          </button>
        </div>
      </template>
    </template>

    <!-- Footer com navegação e ações -->
    <template #footer>
      <div class="d-flex flex-wrap gap-2 w-100 align-items-center">
        <!-- Navegação entre itens -->
        <div class="d-flex gap-1">
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="anterior" :disabled="!temAnterior || isLoadingDetalhes" title="Anterior">
            <i class="bi bi-chevron-left"></i>
          </button>
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="proximo" :disabled="!temProxima || isLoadingDetalhes" title="Próximo">
            <i class="bi bi-chevron-right"></i>
          </button>
        </div>

        <span class="text-body-secondary small me-auto">{{ posicaoLabel }}</span>

        <slot name="footer-extra"></slot>

        <!-- Checkbox de checagem -->
        <div class="form-check form-check-inline mb-0">
          <input class="form-check-input" type="checkbox" id="confirmacaoAtiva" v-model="confirmacaoAtiva">
          <label class="form-check-label small text-body-secondary" for="confirmacaoAtiva">
            Habilitar Checagem de Aprovação
          </label>
        </div>

        <!-- Decisões customizadas (substituem Aprovar/Reprovar quando existem) -->
        <template v-if="temDecisoesCustomizadas && itemDetalhes">
          <button
            v-for="opcao in decisoesCustomizadas"
            :key="opcao.decisao || opcao.Decisao"
            type="button"
            class="btn btn-sm flex-fill flex-md-grow-0"
            :class="(opcao.status || opcao.Status) === 'APROVAR' ? 'btn-success' : 'btn-outline-danger'"
            @click="executarDecisaoCustomizada(opcao)"
            :disabled="isLoadingDetalhes || estaProcessandoAcao"
          >
            <i :class="(opcao.status || opcao.Status) === 'APROVAR' ? 'bi bi-check-circle me-1' : 'bi bi-x-circle me-1'"></i>
            {{ opcao.labelBotao || opcao.LabelBotao }}
          </button>
        </template>

        <!-- Fallback padrão: Aprovar/Reprovar -->
        <template v-else>
          <button type="button" class="btn btn-outline-danger btn-sm flex-fill flex-md-grow-0" @click="confirmarReprovacao" :disabled="isLoadingDetalhes || estaProcessandoAcao">
            <i class="bi bi-x-circle me-1"></i> Reprovar
          </button>
          <button type="button" class="btn btn-success btn-sm flex-fill flex-md-grow-0" @click="confirmarAprovacao" :disabled="isLoadingDetalhes || estaProcessandoAcao">
            <i class="bi bi-check-circle me-1"></i> Aprovar
          </button>
        </template>
      </div>
    </template>
  </ModalFormularioV2>

  <!-- ConfirmationModal para Reprovar -->
  <ConfirmationModal
    ref="reprovarModalRef"
    :title="definicao.confirmacaoReprovarTitulo || 'Reprovar'"
    :message="definicao.confirmacaoReprovarMensagem || 'Tem certeza que deseja reprovar?'"
    confirm-button-text="Sim, Reprovar"
    cancel-button-text="Cancelar"
    @confirmed="executarReprovacao"
  />

  <!-- ConfirmationModal para Aprovar -->
  <ConfirmationModal
    ref="aprovarModalRef"
    :title="definicao.confirmacaoAprovarTitulo || 'Aprovar'"
    :message="definicao.confirmacaoAprovarMensagem || 'Tem certeza que deseja aprovar?'"
    confirm-button-text="Sim, Aprovar"
    cancel-button-text="Cancelar"
    @confirmed="executarAprovacao"
  />
</template>

<style scoped>
/* === Filtros inline - mobile-first === */
:deep(.filtro-inline-row) {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: flex-end;
}

:deep(.filtro-group) {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

:deep(.filtro-label) {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--bs-secondary-color);
  margin-bottom: 0;
}

:deep(.filtro-numero) {
  flex: 1 1 140px;
  min-width: 120px;
}

:deep(.filtro-resumo) {
  flex: 1 1 160px;
  min-width: 130px;
}

:deep(.filtro-valor) {
  flex: 0 1 auto;
}

:deep(.filtro-grupo) {
  flex: 1 1 100%;
}

:deep(.filtro-acoes) {
  flex: 0 0 auto;
}

:deep(.filtro-grupo .badge) {
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
  transition: all 0.15s ease;
  white-space: nowrap;
}

:deep(.filtro-grupo .badge.text-bg-light) {
  background-color: color-mix(in srgb, var(--bs-secondary-bg) 60%, transparent) !important;
  color: var(--bs-body-color) !important;
  border-color: var(--bs-border-color) !important;
}

:deep(.filtro-grupo .badge:hover) {
  opacity: 0.85;
  transform: translateY(-1px);
}

:deep(.filtro-valor .btn) {
  font-size: 0.72rem;
  padding: 0.2rem 0.5rem;
  white-space: nowrap;
}

/* Scroll itens */
:deep(.itens-scroll-wrapper) {
  max-height: 220px;
  overflow-y: auto;
}

:deep(.itens-scroll-wrapper::-webkit-scrollbar) {
  width: 6px;
}

:deep(.itens-scroll-wrapper::-webkit-scrollbar-thumb) {
  background-color: var(--bs-border-color);
  border-radius: 3px;
}

:deep(.item-row:last-child) {
  border-bottom: none !important;
}

/* Ajustes responsivos */
@media (max-width: 576px) {
  :deep(.filtro-inline-row) {
    gap: 0.4rem;
  }

  :deep(.filtro-numero),
  :deep(.filtro-resumo) {
    flex: 1 1 100%;
    min-width: 0;
  }

  :deep(.filtro-valor) {
    flex: 1 1 100%;
  }

  :deep(.filtro-valor .btn) {
    flex: 1;
    text-align: center;
  }

  :deep(.filtro-grupo .badge) {
    font-size: 0.7rem;
    padding: 0.25rem 0.6rem;
  }

  :deep(.filtro-acoes) {
    flex: 1 1 100%;
  }

  :deep(.filtro-acoes .d-flex) {
    justify-content: flex-end;
  }
}
</style>
